import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { InputNode } from '../types';
import { parseStepsTooltip } from '../utils/stepsTooltip';
import { StepsTutorial } from './StepsTutorial';
import { InfoIconTooltip, isStepsTooltipRaw } from './InfoIconTooltip';

type Props = {
  node: InputNode;
  value: string | undefined;
  onChange: (key: string, value: string) => void;
  onParse?: (text: string) => Promise<void>;
  disabled?: boolean;
  error?: string;
};

type Variant = {
  /** chip 上的扩展 class（rte-markdown-input / rte-markdown-name） */
  chipClass: string;
  /** error 时追加的 class */
  errorClass: string;
  /** data-type 值 */
  dataType: string;
  /** 默认 placeholder */
  defaultPlaceholder: string;
};

/**
 * markdown / markdown-name chip 工厂。
 *
 * V1 结构（markdown/render.ts + markdown-name/render.ts）：chip 本身就是
 * `<span contenteditable class="variable-chip rte-markdown-input ...">`，
 * placeholder 走 `.show-placeholder::after` 伪元素（inline 文本，单行 hug + 超长 break-word）。
 *
 * V2 之前错误地在 chip 内包了 `<textarea>/<input>`：textarea 是多行控件，
 * CJK placeholder 在其固有宽度内换行 → chip 变高变胖（与 V1 单行不一致）。
 * 实测（headless Chrome）：chip 自身 contentEditable 时 236×30 单行 hug，与 V1 一致。
 *
 * 受控同步：useLayoutEffect 命令式写 textContent，仅当外部 value 与 DOM 现状不一致
 * 才回写（避免每次输入重写 DOM 把光标弹回开头）。onInput 回读 textContent。
 *
 * in-chip info-icon：因 chip 用 textContent 同步会销毁内部 React 子元素，icon 不能放
 * chip 内。改用 relative host 包裹 chip + icon，icon 绝对定位到 host 右侧（host inline-block
 * hug chip 宽度，故 right:4px ≈ chip 右缘）；chip 的 .rte-variable-with-icon padding-right
 * 仍预留右侧空间避免 placeholder 跑到 icon 下。
 */
export function createMarkdownChipNode(variant: Variant, displayName: string) {
  const Comp: React.FC<Props> = React.memo(
    ({ node, value, onChange, onParse, disabled, error }) => {
      const ref = useRef<HTMLSpanElement>(null);
      const [parsing, setParsing] = useState(false);
      // 解析成功后本 chip 会被 effectiveConfig 从渲染树移除（输入框消失，只留 module）。
      // await onParse 完成时组件可能已卸载，finally 的 setParsing 会对已卸载组件 setState。
      // 用 mounted ref 守卫，避免 React 警告。
      const mountedRef = useRef(true);
      useLayoutEffect(() => {
        mountedRef.current = true;
        return () => {
          mountedRef.current = false;
        };
      }, []);
      const text = value ?? '';

      // 仅当外部值与 DOM 现状不同才回写，避免输入时光标跳到开头。
      useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (!text) {
          if (el.innerHTML !== '') el.innerHTML = '';
        } else if (el.textContent !== text) {
          el.textContent = text;
        }
      }, [text]);

      const handleInput = useCallback(
        (e: React.FormEvent<HTMLSpanElement>) => {
          if (!node.key) return;
          onChange(node.key, e.currentTarget.textContent ?? '');
        },
        [node.key, onChange],
      );

      // 实际解析：标记 loading → 调 onParse。供 onBlur / onPaste 共用。
      const runParse = useCallback(
        async (raw: string) => {
          const trimmed = raw.trim();
          if (!trimmed || !onParse) return;
          setParsing(true);
          try {
            await onParse(trimmed);
          } finally {
            if (mountedRef.current) setParsing(false);
          }
        },
        [onParse],
      );

      const onBlur = useCallback(() => runParse(text), [runParse, text]);

      // V1 parity：粘贴即解析（不必等失焦）。粘贴文本直接替换 chip 内容 + 回写 value，
      // 立即触发解析；解析中 effectiveConfig 会把本 chip 换成「解析中...」占位。
      const onPaste = useCallback(
        (e: React.ClipboardEvent<HTMLSpanElement>) => {
          if (!onParse || !node.key) return;
          const pasted = e.clipboardData.getData('text/plain');
          if (!pasted.trim()) return;
          e.preventDefault();
          if (ref.current) ref.current.textContent = pasted;
          onChange(node.key, pasted);
          void runParse(pasted);
        },
        [onParse, node.key, onChange, runParse],
      );

      const isEmpty = !value;
      const classes = ['variable-chip', variant.chipClass];
      if (isEmpty) classes.push('show-placeholder');
      if (error) classes.push(variant.errorClass);

      // steps tooltip 走「查看教程」按钮（chip 同级渲染），普通字符串 tooltip 走 in-chip info-icon。
      const stepsTooltip = parseStepsTooltip(node.tooltip);
      const tooltip =
        node.tooltip == null
          ? ''
          : typeof node.tooltip === 'string'
          ? node.tooltip
          : JSON.stringify(node.tooltip);
      const hasInChipIcon = !!tooltip && !isStepsTooltipRaw(tooltip);
      if (hasInChipIcon && isEmpty) classes.push('rte-variable-with-icon');

      const placeholder = node.placeholder ?? variant.defaultPlaceholder;

      const chipSpan = (
        <span
          ref={ref}
          className={classes.join(' ')}
          data-type={variant.dataType}
          data-key={node.key}
          data-placeholder={placeholder}
          contentEditable={!(disabled || parsing)}
          suppressContentEditableWarning
          onInput={handleInput}
          onBlur={onBlur}
          onPaste={onPaste}
        />
      );

      // 有 in-chip icon 时用 relative host 包裹 chip + icon（icon 不能放进 contentEditable）。
      const chipWithIcon = hasInChipIcon ? (
        <span className="rte-markdown-host">
          {chipSpan}
          <InfoIconTooltip
            tooltip={tooltip}
            className="variable-info-icon"
            hidden={!isEmpty}
            dataVariableKey={node.key}
          />
        </span>
      ) : (
        chipSpan
      );

      const chipEl = (
        <>
          {chipWithIcon}
          {stepsTooltip ? (
            <StepsTutorial data={stepsTooltip} hidden={!isEmpty} />
          ) : null}
        </>
      );

      // V1 parity（required 分支）：required 时外套 exclusion-wrapper + ⛤ 必填 icon。
      if (!node.required) return chipEl;
      return (
        <span
          className="exclusion-wrapper required-upload-group"
          data-group={node.exclusionGroup}
        >
          <span className="rte-text-required">
            <i className="iconfont icon-bitian" style={{ fontSize: 10 }} />
          </span>
          {chipEl}
        </span>
      );
    },
  );
  Comp.displayName = displayName;
  return Comp;
}
